import React from 'react';
import { 
  Facebook, 
  Linkedin, 
  MessageCircle, 
  Send,
  Share2
} from 'lucide-react';

interface ShareButtonsProps {
  url: string;
  title: string;
  description?: string;
  hashtags?: string[];
  className?: string;
  variant?: 'compact' | 'full';
}

const ShareButtons: React.FC<ShareButtonsProps> = ({
  url,
  title,
  description = '',
  hashtags = [],
  className = '',
  variant = 'full'
}) => {
  // console.log('[LOG_RSS] ShareButtons initialized with props:', {
    // url,
    // title,
    // description,
    // hashtags,
    // className,
    // variant
  // }); // Removed for production

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  const encodedHashtags = hashtags.map(tag => `%23${tag.replace('#', '')}`).join('');

  // console.log('[LOG_RSS] Encoded values:', {
    // encodedUrl,
    // encodedTitle,
    // encodedDescription,
    // encodedHashtags
  // }); // Removed for production

  // Custom X (Twitter) icon component
  const XIcon = () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );

  const shareData = {
    twitter: {
      url: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}${encodedHashtags ? `&hashtags=${encodedHashtags}` : ''}`,
      icon: XIcon,
      color: 'bg-black hover:bg-gray-800',
      label: 'X'
    },
    facebook: {
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: Facebook,
      color: 'bg-blue-600 hover:bg-blue-700',
      label: 'Facebook'
    },
    linkedin: {
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      icon: Linkedin,
      color: 'bg-blue-700 hover:bg-blue-800',
      label: 'LinkedIn'
    },
    whatsapp: {
      url: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      icon: MessageCircle,
      color: 'bg-green-500 hover:bg-green-600',
      label: 'WhatsApp'
    },
    telegram: {
      url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
      icon: Send,
      color: 'bg-blue-400 hover:bg-blue-500',
      label: 'Telegram'
    }
  };

  // console.log('[LOG_RSS] Share data URLs generated:', {
    // twitter: shareData.twitter.url,
    // facebook: shareData.facebook.url,
    // linkedin: shareData.linkedin.url,
    // whatsapp: shareData.whatsapp.url,
    // telegram: shareData.telegram.url
  // }); // Removed for production

  // Log additional metadata that might affect image sharing
  // console.log('[LOG_RSS] Additional metadata for debugging:', {
    // currentUrl: window.location.href,
    // documentTitle: document.title,
    // metaTags: {
      // ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute('content'),
      // ogDescription: document.querySelector('meta[property="og:description"]')?.getAttribute('content'),
      // ogImage: document.querySelector('meta[property="og:image"]')?.getAttribute('content'),
      // ogUrl: document.querySelector('meta[property="og:url"]')?.getAttribute('content'),
      // twitterCard: document.querySelector('meta[name="twitter:card"]')?.getAttribute('content'),
      // twitterImage: document.querySelector('meta[name="twitter:image"]')?.getAttribute('content')
    // }
  // }); // Removed for production

  const handleShare = (platform: keyof typeof shareData) => {
    // console.log(`[LOG_RSS] handleShare called for platform: ${platform}`); // Removed for production
    
    // Check meta tags RIGHT BEFORE sharing
    const currentMetaTags = {
      ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute('content'),
      ogDescription: document.querySelector('meta[property="og:description"]')?.getAttribute('content'),
      ogImage: document.querySelector('meta[property="og:image"]')?.getAttribute('content'),
      ogUrl: document.querySelector('meta[property="og:url"]')?.getAttribute('content'),
      twitterCard: document.querySelector('meta[name="twitter:card"]')?.getAttribute('content'),
      twitterImage: document.querySelector('meta[name="twitter:image"]')?.getAttribute('content')
    };
    
    // console.log(`[LOG_RSS] Meta tags at sharing time for ${platform}:`, currentMetaTags); // Removed for production
    
    if (!currentMetaTags.ogImage) {
      // console.warn(`[LOG_RSS] ⚠️ WARNING: No OG Image found when sharing to ${platform}!`); // Removed for production
    } else {
      // console.log(`[LOG_RSS] ✅ OG Image found when sharing to ${platform}:`, currentMetaTags.ogImage); // Removed for production
    }
    
    const shareInfo = shareData[platform];
    // console.log(`[LOG_RSS] Opening share URL for ${platform}:`, shareInfo.url); // Removed for production
    // console.log(`[LOG_RSS] Share info object:`, shareInfo); // Removed for production
    
    try {
      window.open(shareInfo.url, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
      // console.log(`[LOG_RSS] Successfully opened share window for ${platform}`); // Removed for production
    } catch (error) {
      console.error(`[LOG_RSS] Error opening share window for ${platform}:`, error);
    }
  };

  const handleNativeShare = async () => {
    // console.log('[LOG_RSS] handleNativeShare called'); // Removed for production
    // console.log('[LOG_RSS] Navigator.share available:', !!navigator.share); // Removed for production
    // console.log('[LOG_RSS] Native share data:', { title, text: description, url }); // Removed for production
    
    if (navigator.share) {
      try {
        // console.log('[LOG_RSS] Attempting native share...'); // Removed for production
        await navigator.share({
          title,
          text: description,
          url
        });
        // console.log('[LOG_RSS] Native share successful'); // Removed for production
      } catch (error) {
        // console.log('[LOG_RSS] Error in native share:', error); // Removed for production
      }
    } else {
      // console.log('[LOG_RSS] Native share not available, falling back to clipboard'); // Removed for production
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(url);
        // console.log('[LOG_RSS] URL copied to clipboard successfully'); // Removed for production
        alert('URL copiada al portapapeles!');
      } catch (error) {
        // console.log('[LOG_RSS] Error copying to clipboard:', error); // Removed for production
      }
    }
  };

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          onClick={() => {
            // console.log('[LOG_RSS] X (Twitter) button clicked (compact variant)'); // Removed for production
            handleShare('twitter');
          }}
          className="p-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
          title="Compartir en X"
        >
          <XIcon />
        </button>
        <button
          onClick={() => {
            // console.log('[LOG_RSS] Facebook button clicked (compact variant)'); // Removed for production
            handleShare('facebook');
          }}
          className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
          title="Compartir en Facebook"
        >
          <Facebook size={16} />
        </button>
        <button
          onClick={() => {
            // console.log('[LOG_RSS] WhatsApp button clicked (compact variant)'); // Removed for production
            handleShare('whatsapp');
          }}
          className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
          title="Compartir en WhatsApp"
        >
          <MessageCircle size={16} />
        </button>
        <button
          onClick={() => {
            // console.log('[LOG_RSS] Native share button clicked (compact variant)'); // Removed for production
            handleNativeShare();
          }}
          className="p-2 bg-gray-500 text-white rounded-full hover:bg-gray-600 transition-colors"
          title="Compartir"
        >
          <Share2 size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">Compartir:</span>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            // console.log('[LOG_RSS] X (Twitter) button clicked (full variant)'); // Removed for production
            handleShare('twitter');
          }}
          className="flex items-center gap-2 px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
          title="Compartir en X"
        >
          <XIcon />
          <span>X</span>
        </button>
        
        <button
          onClick={() => {
            // console.log('[LOG_RSS] Facebook button clicked (full variant)'); // Removed for production
            handleShare('facebook');
          }}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          title="Compartir en Facebook"
        >
          <Facebook size={16} />
          <span>Facebook</span>
        </button>
        
        <button
          onClick={() => {
            // console.log('[LOG_RSS] LinkedIn button clicked (full variant)'); // Removed for production
            handleShare('linkedin');
          }}
          className="flex items-center gap-2 px-3 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors text-sm"
          title="Compartir en LinkedIn"
        >
          <Linkedin size={16} />
          <span>LinkedIn</span>
        </button>
        
        <button
          onClick={() => {
            // console.log('[LOG_RSS] WhatsApp button clicked (full variant)'); // Removed for production
            handleShare('whatsapp');
          }}
          className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
          title="Compartir en WhatsApp"
        >
          <MessageCircle size={16} />
          <span>WhatsApp</span>
        </button>
        
        <button
          onClick={() => {
            // console.log('[LOG_RSS] Telegram button clicked (full variant)'); // Removed for production
            handleShare('telegram');
          }}
          className="flex items-center gap-2 px-3 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors text-sm"
          title="Compartir en Telegram"
        >
          <Send size={16} />
          <span>Telegram</span>
        </button>
        
        <button
          onClick={() => {
            // console.log('[LOG_RSS] Native share button clicked (full variant)'); // Removed for production
            handleNativeShare();
          }}
          className="flex items-center gap-2 px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
          title="Compartir"
        >
          <Share2 size={16} />
          <span>Más</span>
        </button>
      </div>
    </div>
  );
};

export default ShareButtons; 